/**
 * register.js
 */

import React from 'react';
import axios from 'axios';
import { Link, useLocation } from "react-router-dom";
import makeStyles from '@mui/styles/makeStyles';
import {
  CssBaseline,
  Grid
} from '@mui/material';
import Alert from '@mui/material/Alert';
import MenuIcon from '@mui/icons-material/Menu';
import { useForm, useWatch, FormProvider } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { stripePromise } from 'stripe';
import { Types } from 'utils/constants';
import { randomInt } from 'utils/random';
import { 
  emailValidationRules,
  passwordValidationRules,
  passwordConfirmValidationRules,
  firstNameValidationRules,
  lastNameValidationRules,
  departmentNameValidationRules,
} from 'utils/user-profile-validation';
import Input from './input';
import CheckboxList from './checkbox-list';
import Select from './select';
import Spinner from 'components/spinner';
import logoBadge from 'assets/images/logo-badge.png';
import logoText from 'assets/images/logo-text.png';
import {
  SubscriptionType,
  PaymentType,
} from 'routing/routes/config/billing/billing-constants';


const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: 'white',
    maxHeight: '100vh',
    overflowY: 'auto',
    overflowX: 'hidden',
    '& a': {
      color: '#2e96e1',
      '&:hover': {
        color: 'grey'
      }      
    }
  },
  invisible: {
    height: '0px',
    transform: 'scale(0.000001)'
  },
  nav: {
    backgroundColor: '#0e173b',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    borderBottom: `6px solid white`,
    '& > *': {
      width: '88%',
      maxWidth: 1200,
      padding: '19px 0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    '& .logo': {
      display: 'flex',
      alignItems: 'center',
      '& .logo-badge': {
        maxHeight: 52,
        marginRight: 7
      },
      '& .logo-text': {
        maxHeight: 30
      }
    },
    '& .menu-desktop': {
      display: 'flex',
      alignItems: 'center',
      marginRight: -16,
      '& ul': {
        listStyle: 'none',
        display: 'flex',       
        '& li': {
          '& a': {
            padding: '20px 15px',
            color: 'hsl(0,0%,100%)',
            textDecoration: 'none',
            fontFamily: `'Lato',sans-serif`,
            fontStyle: 'normal',
            fontSize: '14px',
            fontWeight: 400,
            letterSpacing: '0.085em',
            textTransform: 'uppercase',
            '& span': {
              padding: '0 0 5px'
            },
            '&:hover, &.active': {
              color: '#9ca3ba',
              '& span': {
                boxShadow: '0 2px 0 0 #9ca3ba'
              }
            }
          }
        }
      },
      [theme.breakpoints.down(980)]: {
        display: 'none'
      }
    },
    '& .menu-mobile': {
      display: 'none',
      alignItems: 'flex-start',
      height: 0,
      padding: 0,
      overflow: 'hidden',
      transition: 'height 300ms cubic-bezier(0.4, 0, 0.2, 1)',
      '& ul': {
        flex: 1,
        margin: 0,        
        padding: '8px 0',
        listStyle: 'none',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: `"Lato",sans-serif`,
        fontStyle: 'normal',
        fontWeight: 400,
        letterSpacing: '0.085em',
        textTransform: 'uppercase',
        '& li': {
          flex: 1,
          display: 'flex',
          '& a': {
            flex: 1,
            margin: '0 0 11px',
            border: '1px solid white',
            padding: '0.75em 0.95em',
            color: 'white',
            textDecoration: 'none',
            textAlign: 'left',
            '&.active': {
              color: '#9ca3ba',
              borderColor: '#9ca3ba'
            },
          },
        }
      },
      '&.open': {
        height: 350,
      },
      [theme.breakpoints.down(980)]: {
        display: 'flex'
      }
    },
    '& .mobile-menu-button': {
      display: 'none',
      backgroundColor: '#cececf',
      padding: '11px 14px',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      outline: 'none',
      border: 'none',
      borderRadius: 4,
      '& svg': {
        transform: 'scale(1.4)'
      },
      '&:active': {
        backgroundColor: '#bbb'
      },
      [theme.breakpoints.down(980)]: {
        display: 'flex'
      }
    },
    '& .mobile-title': {
      display: 'none',
      justifyContent: 'center',
      backgroundColor: 'white',
      width: '100%',
      padding: '24px 0',
      '& h1': {
        width: '88%',
        margin: 0,
        fontFamily: `"Raleway",sans-serif`,
        fontStyle: 'normal',
        fontSize: '245%',
        fontWeight: 400,
        color: 'hsl(0,0%,11%)',
        letterSpacing: '-0.075em',
        textAlign: 'left',
        [theme.breakpoints.down(768)]: {
          textAlign: 'center'
        }
      },
      [theme.breakpoints.down(980)]: {
        display: 'flex',
      }
    },
    [theme.breakpoints.down(980)]: {
      borderBottom: 0,
    }
  },
  banner: {
    backgroundColor: '#0e173b',
    color: `rgb(245, 245, 248)`,
    padding: `30px 30px 40px 30px`,
    fontFamily: `"Raleway",sans-serif`,
    fontStyle: `normal`,
    '& .title': {
      fontSize: `285.7%`,
      fontWeight: 300,
      lineHeight: `1.3`,
      margin: 0
    },
    '& .steps': {
      marginTop: 12,
      fontSize: `170%`,
      fontWeight: 300,
      lineHeight: `1.5`
    },
  },
  form: {
    padding: `85px 20px`,
    [theme.breakpoints.down(980)]: {
      padding: `20px`,
    },
    fontFamily: `"Raleway",sans-serif`,
    fontStyle: `normal`,
    letterSpacing: 'normal',
    '& .contact': {
      color: '#2e96e1',
      textDecoration: 'underline',
      cursor: 'pointer',
      '&:hover': {
        color: 'grey'
      }
    },
    '& .form-banner': {
      marginBottom: 40,
      '& .form-banner-title': {
        margin: 0,
        marginBottom: 12,
        fontSize: `250%`,
        fontWeight: 300,
        color: 'hsl(0,0%,11%)'
      },
      '& .form-banner-subtitle': {
        margin: 0,
        marginBottom: 20,
        fontSize: '118%',
        fontWeight: 300,
        color: 'hsl(0,0%,11%)',
        lineHeight: '1.5',
      },
      '& .form-banner-list': {
        margin: 0,
        marginBottom: 28,
        padding: 0,
        listStyle: 'none',
        fontSize: `1.3em`,
        fontWeight: 500,
        lineHeight: `1.3em`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        '& li': {
          marginBottom: 5,
          '& svg': {
            backgroundColor: 'black',
            color: 'white',
            borderRadius: 20,
            transform: 'scale(0.8) translateY(7px)'
          }
        }
      }
    },
    '& .form-inputs': {
      maxWidth: 700,
      margin: '0 auto',
      textAlign: 'left',
      '& .MuiGrid-item': {
        paddingTop: 10,
        paddingBottom: 10,
        display: 'flex',
        flexDirection: 'column',
      },
      '& label': {
        marginBottom: 5,
      },
      '& input, & select': {
        flex: 1,
        minHeight: 50,
        maxHeight: 50,
        padding: `0 0.65em`,
        fontSize: '16px',
        fontFamily: `"Raleway",sans-serif`,
        lineHeight: 'normal',
        color: '#555',
        borderRadius: 3,
        border: `2px solid #ddd`,
        outline: 'none',
        transition: `200ms linear`,
        boxSizing: 'border-box',
        '&:focus': {
          border: `2px solid rgba(0,0,0,0.25)`,
        }
      },
      '& .error': {
        marginTop: 2,
        color: '#ff1717',
        fontSize: '0.9em',
      },
      '& .rolesContainer': {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        '& > *': {
          display: 'flex',
          alignItems: 'center',
          minWidth: 150,
        },
        '& input': {
          transform: 'translateY(-2px) translateX(3px)',
          width: 20,
          height: 20,
          minHeight: 'unset',
          flex: 'unset'
        },
        '& label': {
          fontSize: '1.4em',
          marginLeft: 13
        }
      },
      '& .verificationContainer': {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        fontFamily: 'helvetica',
        fontSize: 20,
        '& input': {
          marginLeft: 7,
          maxWidth: 100
        }
      },
      '& .submit': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 25,
        minHeight: 62,
        maxHeight: 62,
        fontSize: '1.5em',
        fontWeight: 'bold',
        color: 'white',
        borderColor: 'hsla(200,100%,60%,0)',
        borderRadius: '.25em',
        backgroundColor: '#81a8c4',
        textShadow: '0 0.075em 0.075em rgb(0 0 0 / 50%)',
        cursor: 'pointer',
        transition: 'all 150ms linear',
        '&:hover': {
          borderColor: 'hsla(0,0%,63%,0)',
          backgroundColor: '#aeb2b4',
          textShadow: '0 0.075em 0.075em rgb(0 0 0 / 50%)',
        }
      },
    }
  },
  footer: {
    backgroundColor: '#0e173b',
    color: 'white',
    padding: '50px 20px',
    fontSize: '11px',
    fontFamily: `"Raleway",sans-serif`,
    fontWeight: 400,
    letterSpacing: '1px',
    lineHeight: '1.3',
    '& a': {
      color: 'white',
      textDecoration: 'none',
      letterSpacing: '1px',
      '&:hover': {
        opacity: '0.35',
      }
    },
    '& .menu': {
      listStyle: 'none',
      margin: '20px 0',
      padding: 0,
      fontStyle: 'normal',
      textTransform: 'uppercase',
      display: 'flex',
      justifyContent: 'center',
      flexWrap: 'wrap',
      '& li': {
        '&:before': {
          content: `"/"`,
          margin: '0 6px',
          opacity: '0.35'
        },
        '&:first-child:before': {
          content: `""`
        },
      }
    },
    '& .copyright': {
      marginBottom: 18
    }
  }
}));


const menuItems = [{
  label: 'Home',
  link: 'https://packtrackapp.com'
}, {
  label: 'Features',
  link: 'https://packtrackapp.com/features'
}, {
  label: 'Get Started',
  link: 'https://packtrackapp.com/get-started',
  class: 'active'
}, {
  label: 'About',
  link: 'https://packtrackapp.com/about'
}, {
  label: 'Blog',
  link: 'https://packtrackapp.com/blog'
}, {
  label: 'Log In',
  link: '/'
}]


const roleTypes = [{
  id: 'handler',
  label: 'Handler',
  value: Types.Role.HANDLER,
}, {
  id: 'supervisor',
  label: 'Supervisor',
  value: Types.Role.SUPERVISOR,
}, {
  id: 'trainer',
  label: 'Trainer',
  value: Types.Role.TRAINER
}];


const subscriptionTypes = [{
  id: 'trial',
  label: '30 Day Free Trial',
  value: SubscriptionType.TRIAL
}, {
  id: 'monthly',
  label: 'Monthly ($14/month)',
  value: SubscriptionType.MONTHLY
}, {
  id: 'yearly',
  label: 'Yearly ($140/year)',
  value: SubscriptionType.YEARLY
}];


const paymentTypes = [{
  id: 'cc',
  label: 'Credit Card',
  value: PaymentType.AUTOMATIC
}, {
  id: 'invoice',
  label: 'Invoice',
  value: PaymentType.REMITTANCE
}];


export default function Register(props) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [verificationValues] = React.useState([randomInt(1, 5), randomInt(1, 5)]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isCompleted, setIsCompleted] = React.useState(false);
  const verificationAnswer = verificationValues[0] + verificationValues[1];
  const formContext = useForm({
    defaultValues: {
      Email: '',
      Password: '',
      ConfirmPassword: '',
      First: '',
      Last: '',
      DepartmentName: '',
      Roles: [Types.Role.HANDLER],
      SubscriptionType: '1',
      PaymentType: '1',
      // security fields
      Age: '',
      Answer: verificationAnswer,
      Verification: ''
    }
  });
  const { control, handleSubmit, setValue, setError, formState: { errors } } = formContext;
  const roles = useWatch({ control, name: 'Roles' });
  const subType = Number(useWatch({ control, name: 'SubscriptionType' }));
  const password = useWatch({ control, name: 'Password' });


  // Check to see if the user is logged in already. The location change and the timestamp param are to ensure that
  // the effect runs and loads fresh data when returning to the page by navigating back.
  React.useEffect(() => {
    (async () => {
      const response = await axios.get(`/account/check-login?timestamp=${new Date().getTime()}`);
      if (response.data) {
        setIsCompleted(true);
      }  
      setIsLoading(false);
    })();
  }, [location]);


  React.useEffect(() => {
    document.title = 'Sign Up For A Free Trial To The Mobile App & Website - PACKTRACK';

    // intercom init
    window.intercomSettings = {
      app_id: "o89sg5k0",
    };
  }, []);


  const onSubmit = async (data) => {
    setIsSubmitting(true);
    const response = await axios.post(`/account/register`, data);
    if (response.status === 200) {
      // set server error(s)
      for (let key in response.data.errors) {
        setError(key, {
          type: "manual",
          message: response.data.errors[key]
        });
      }
    } else if (response.status === 201) {
      const paymentSessionId = response.data.paymentSessionId;
      if (paymentSessionId) {
        const stripe = await stripePromise;
        const { error } = await stripe.redirectToCheckout({
          sessionId: paymentSessionId,
        });
        if (error) {
          enqueueSnackbar(`Error loading payment form. ${error.message}`, {
            variant: 'error',
            persist: true,
            anchorOrigin: {
              vertical: "top",
              horizontal: "center"
            }
          });
        }
      } else {
        window.location = response.data.redirectUrl;
      }
    }
    setIsSubmitting(false);
  }


  return <>
    <CssBaseline />

    {/* FONTS & CSS */}
    <link href='http://fonts.googleapis.com/css?family=Lato:400,700' rel='stylesheet' type='text/css' />
    <link href="https://fonts.cdnfonts.com/css/raleway-5" rel="stylesheet" />
    <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@splidejs/splide@4.1.4/dist/css/splide-core.min.css" />

    <div className={classes.root}>

      {/* HEADER */}
      <header className={classes.nav}>
        <div>
          <a className='logo' href='https://packtrackapp.com'>
            <img src={logoBadge} className='logo-badge' alt='PACKTRACK Badge Logo' />
            <img src={logoText} className='logo-text' alt='PACKTRACK Text Logo' />
          </a>
          <div className='menu-desktop'>
            <ul>
              {menuItems.map(mi => (
                <li key={mi.label}>
                  <a href={mi.link} className={mi.class || ''}><span>{mi.label}</span></a>
                </li>
              ))}
            </ul>
          </div>
          <button 
            className='mobile-menu-button'
            children={<MenuIcon />}
            onClick={() => setMobileMenuOpen(p => !p)}
          />
        </div>
        <div className={`menu-mobile ${mobileMenuOpen ? 'open' : ''}`}>
          <ul>
            {menuItems.map(mi => (
              <li key={mi.label}>
                <a href={mi.link} className={mi.class || ''}><span>{mi.label}</span></a>
              </li>
            ))}
          </ul>
        </div>
        <div className='mobile-title'>
          <h1>Get Started</h1>
        </div>
      </header>

      <div className={classes.form}>
        {!isLoading &&
          <>
            <div className='form-banner'>
              <h2 className='form-banner-title'><strong>PACKTRACK</strong> Sign Up</h2>
              <h5 className='form-banner-subtitle'>
                {isCompleted
                  ? <div>You have already created a PackTrack account. Visit <Link to='/'>mypacktrack.com</Link> to continue.</div>
                  : <>
                    <div>Get full access to the PACKTRACK app and website. No contract required!</div>
                    <div>All pricing is for handlers only. Trainer and Supervisor subscriptions are completely free.</div>
                  </>
                }
              </h5>
              <h4>Have questions?&nbsp;
                <span
                  className='contact'
                  onClick={() => window.Intercom('showNewMessage')}>Chat with us!
                </span>
              </h4>
            </div>

            {/* FORM */}
            {!isCompleted &&
              <>
                <div className='form-inputs'>
                  <FormProvider {...formContext}>
                    <form noValidate onSubmit={handleSubmit(onSubmit)}>
                      <Grid container spacing={3}>

                        {/* AGE HONEYPOT (hidden from user and used to detect bots / prevent signup) */}
                        <div className={classes.invisible}>
                          <Input
                            name='Age'
                            type='text'
                            autocomplete='new-age'
                            rules={{ validate: val => val !== '' ? '' : true }}
                          />
                        </div>

                        {/* EMAIL */}
                        <Grid item xs={12}>
                          <label>Email</label>
                          <Input
                            name='Email'
                            type='email'
                            rules={emailValidationRules()}
                          />
                          <span className='error'>{errors.Email?.message || ''}</span>
                        </Grid>

                        {/* PASSWORD1 */}
                        <Grid item xs={12} sm={6}>
                          <label>Password</label>
                          <Input
                            name='Password'
                            type='password'
                            rules={passwordValidationRules(false)}
                          />
                          <span className='error'>{errors.Password?.message || ''}</span>
                        </Grid>

                        {/* PASSWORD2 */}
                        <Grid item xs={12} sm={6}>
                          <label>Confirm Password</label>
                          <Input
                            name='ConfirmPassword'
                            type='password'
                            rules={passwordConfirmValidationRules(false, password)}
                          />
                          <span className='error'>{errors.ConfirmPassword?.message || ''}</span>
                        </Grid>

                        {/* FIRST NAME */}
                        <Grid item xs={12} sm={4}>
                          <label>First Name</label>
                          <Input
                            name='First'
                            type='text'
                            rules={firstNameValidationRules()}
                          />
                          <span className='error'>{errors.First?.message || ''}</span>
                        </Grid>

                        {/* LAST NAME */}
                        <Grid item xs={12} sm={4}>
                          <label>Last Name</label>
                          <Input
                            name='Last'
                            type='text'
                            rules={lastNameValidationRules()}
                          />
                          <span className='error'>{errors.Last?.message || ''}</span>
                        </Grid>

                        {/* DEPARTMENT NAME */}
                        <Grid item xs={12} sm={4}>
                          <label>Department</label>
                          <Input
                            name='DepartmentName'
                            type='text'
                            rules={departmentNameValidationRules()}
                          />
                          <span className='error'>{errors.DepartmentName?.message || ''}</span>
                        </Grid>

                        {/* ROLES */}
                        <Grid item xs={12}>
                          <label>Roles</label>
                          <div className='rolesContainer'>
                            <CheckboxList
                              name='Roles'
                              options={roleTypes}
                              rules={{
                                validate: val => val.length === 0
                                  ? 'You must choose at least one role'
                                  : true
                              }}
                            />
                          </div>
                          <span className='error'>{errors.Roles?.message || ''}</span>
                        </Grid>

                        {/* SUBSCRIPTION TYPE */}
                        <Grid item xs={12} sm={6}>
                          <label>Subscription</label>
                          {roles.includes(Types.Role.HANDLER) &&
                            <Select
                              name='SubscriptionType'
                              options={subscriptionTypes}
                              onChange={(e, value) => {
                                if ([SubscriptionType.MONTHLY, SubscriptionType.TRIAL].includes(Number(value))) {
                                  setValue('PaymentType', PaymentType.AUTOMATIC);
                                }
                              }}
                            />}
                          {!roles.includes(Types.Role.HANDLER) &&
                            <Alert severity='info' children='FREE' />}
                        </Grid>

                        {/* PAYMENT TYPE */}
                        {subType !== SubscriptionType.TRIAL &&
                          <Grid item xs={12} sm={6}>
                            <label>Payment Method</label>
                            <Select
                              name='PaymentType'
                              options={paymentTypes}
                              disabled={subType === SubscriptionType.MONTHLY}
                            />
                          </Grid>}

                        {/* VERIFICATION */}
                        <Grid item xs={12}>
                          <label>Verification - Please answer the following question</label>
                          <Input name='Answer' type='hidden' />
                          <span className='verificationContainer'>
                            {verificationValues[0]} + {verificationValues[1]} =
                            <Input
                              name='Verification'
                              type='text'
                              rules={{
                                required: 'You must answer the verification question',
                                validate: val => parseInt(val) !== verificationAnswer
                                  ? 'Incorrect verification answer'
                                  : true
                              }}
                            />
                          </span>
                          <span className='error verification'>{errors.Verification?.message || ''}</span>
                        </Grid>

                        {/* SUBMIT */}
                        <Grid item xs={12}>
                          {!isSubmitting && <input className='submit' type='submit' value='Sign Up!' />}
                          {isSubmitting && <div className='submit spinner'><Spinner fill='#ffffff' /></div>}
                        </Grid>

                      </Grid>
                    </form>
                  </FormProvider>
                </div>
              </>
            }
          </>
        }
      </div>

      {/* FOOTER */}
      <div className={classes.footer}>
        <ul className='menu'>
          {menuItems.map(mi => (
            <li key={mi.label}>
              <a href={mi.link} className={mi.class || ''}><span>{mi.label}</span></a>
            </li>
          ))}
        </ul>
        <div className='copyright'>
          © {new Date().getFullYear()} PACKTRACK, All Rights Reserved.
        </div>
        <p>
          <a href="https://packtrackapp.com/terms-of-use/">Terms Of Use</a>
          &nbsp;&nbsp;&nbsp;
          <a href="https://packtrackapp.com/user-agreement/">User Agreement</a>
          &nbsp;&nbsp;&nbsp;
          <a href="https://packtrackapp.com/privacy-policy/">Privacy Policy</a>
        </p>
      </div>
      
    </div>
  </>;
}